import { getTimeIntervalFromPeriod } from '@/utils/time-series.utils';
import { subscriptionDateFilter } from '@/utils/subscription-time';
import { common as c } from '@pendo/aggregations';
import moment from '@utils/moment-wrapper';
import { validPeriodsForCount } from '@/utils/date-range.utils';

// Helper function to determine granularity from period
function getGranularityFromPeriod (
    period: c.TimeSeriesPeriodType | 'quarterly' | 'quarterRange',
): moment.unitOfTime.DurationConstructor {
    const interval = getTimeIntervalFromPeriod(period as c.TimeSeriesPeriodType);

    let units: moment.unitOfTime.DurationConstructor = 'days';
    if (interval === 'hour') {
        units = 'hours';
    } else if (interval === 'day') {
        units = 'days';
    } else if (interval === 'week') {
        units = 'weeks';
    } else if (interval === 'month') {
        units = 'months';
    } else if (interval === 'quarter') {
        units = 'quarters';
    }

    return units;
}

export class VanillaTimeSeries {
    period: c.TimeSeriesPeriodType;

    first: number | string;

    count: number;

    constructor (config: c.TimeSeries = {}) {
        this.period = config.period || 'dayRange';
        this.first = config.first || 0;
        this.count = config.count || 1;
    }

    /**
     * Get the first day of the time series.
     */
    firstDay (): number | string {
        return this.first;
    }

    /**
     * Get the start of the day after the last day in the time series.
     */
    last (): number {
        const granularity = getGranularityFromPeriod(this.period);

        return moment(this.first)
            .add(this.count, granularity)
            .valueOf();
    }

    /**
     * Get the last day of the time series.
     */
    lastDay (): number {
        // const granularity = getGranularityFromPeriod(this.period);

        return moment(this.last())
            .subtract(1, 'day')
            .valueOf();
    }

    /**
     * Returns true if the time series includes today
     */
    includesToday (): boolean {
        const today = moment()
            .startOf('day')
            .valueOf();

        return this.last() > today;
    }

    /**
     * Get the periods in the time series.
     */
    periods (): (number | string)[] {
        const { period } = this;
        if (/Range$/.test(period)) {
            return [this.first];
        }

        const granularity = getGranularityFromPeriod(period);
        const periods: (number | string)[] = [];
        const time = moment(this.first);

        for (let i = 0; i < this.count; ++i) {
            periods.push(
                time
                    .clone()
                    .add(i, granularity)
                    .valueOf(),
            );
        }

        return periods;
    }

    /**
     * Get a string representation of the time series.
     */
    toString ({ relative = true }: { relative?: boolean } = {}): string {
        const first = this.firstDay();
        const last = this.lastDay();
        let range = subscriptionDateFilter(first);
        const now = moment();
        const today = 'Today';

        if (first !== last) {
            range += ' - ';
            if (now.isSame(last, 'day') && relative) {
                range += today;
            } else {
                range += subscriptionDateFilter(last);
            }
        }

        return range;
    }

    /**
     * Converts this time series to the equivalent time series with a different period.
     */
    withPeriod (period: c.TimeSeriesPeriodType, ignoreHourlyMultiplier: boolean = false): VanillaTimeSeries {
        if (this.period !== 'daily' && this.period !== 'dayRange') {
            // Only support converting from day-oriented periods for now
            return this;
        }

        const clone = new VanillaTimeSeries({
            period: period as c.TimeSeriesPeriodType,
            first: this.first,
            count: this.count,
        });

        if ((period === 'hourly' || period === 'hourRange') && !ignoreHourlyMultiplier) {
            clone.count *= 24; // Multiply by 24 hours in a day
        } else if (period === 'weekly' || period === 'weekRange') {
            const start = moment(clone.first).startOf('week');
            const end = moment(clone.first)
                .startOf('week')
                .add(clone.count, 'days');
            clone.first = start.unix() * 1000;
            clone.count = Math.ceil(end.diff(start, 'weeks', true));
        } else if (period === 'monthly' || period === 'monthRange') {
            const start = moment(clone.first).startOf('month');
            const end = moment(clone.first)
                .startOf('month')
                .add(clone.count, 'days');
            clone.first = start.unix() * 1000;
            clone.count = Math.ceil(end.diff(start, 'months', true));
        }

        if (clone.count < 1) {
            clone.count = 1;
        }

        return clone;
    }

    /**
     * Get a list of valid periods for this time series.
     */
    validPeriods (): string[] {
        const days = this.count;

        return validPeriodsForCount(days);
    }
}
